import React, { useContext } from 'react';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import { HeartFill, Chat, ShieldFill, StarFill, GraphUpArrow, Fire, PatchCheckFill, LightningFill } from 'react-bootstrap-icons';
import { AppContext } from '../window';
import { useTranslation } from '../i18n';
import AuthRedirect from '../components/AuthRedirect';
import { useEntryModals } from './useEntryModals';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, whitelabel } = useContext(AppContext);
  // Signup/login modals and the `?login=1` / `?signup=1` deep links
  const { openSignup: handleGetStarted, openLogin, modals } = useEntryModals();

  // Show loading indicator while redirecting logged-in users
  if (user) {
    return (
      <>
      <AuthRedirect />
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('general.loading')}</span>
        </div>
      </div>
      </>
    );
  }

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section 
        className="hero-section position-relative overflow-hidden"
        style={{ 
          backgroundImage: 'url(/images/home-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="hero-gradient-overlay"></div>
        <Container className="position-relative z-1 py-5">
          <Row className="justify-content-center text-center py-5">
            <Col lg={10} xl={8}>
              <Badge bg="danger" className="mb-3 px-3 py-2">
                <LightningFill size={16} className="me-1" />
                {t('homepage.members_online_now')}
              </Badge>
              <h1 className="display-2 fw-bold mb-4 text-white hero-title">
                {t('homepage.live_out_fantasies')} <span className="text-gradient">{t('homepage.sexiest_fantasies')}</span>
              </h1>
              <p className="lead mb-5 text-white-50 fs-3">
                {t('homepage.unleash_desires')}
              </p>
              <div className="d-flex justify-content-center flex-wrap gap-4 mb-5">
                <div className="hero-feature-item">
                  <PatchCheckFill className="text-success me-2" />
                  <span className="text-white">{t('homepage.free_to_join')}</span>
                </div>
                <div className="hero-feature-item">
                  <Fire className="text-danger me-2" />
                  <span className="text-white">{t('homepage.hot_singles_waiting')}</span>
                </div>
              </div>
              <Button 
                size="lg" 
                variant="danger" 
                className="hero-cta px-5 py-3 fw-bold"
                onClick={handleGetStarted}
              >
                {t('homepage.create_free_account')}
                <HeartFill className="ms-2" />
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Intro Section */}
      <section className="intro-section py-5">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <Badge bg="danger" className="mb-3">{t('homepage.hot_trending')}</Badge>
              <h2 className="display-5 fw-bold mb-4">
                {t('homepage.craving_passion')} <span className="text-primary">{t('homepage.passion')}</span> {t('homepage.love')}
                <span className="text-danger"> {t('homepage.both')}</span>
              </h2>
              <p className="lead mb-4">
                {t('homepage.thousands_adventurous')}
              </p>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center">
                  <div className="icon-circle bg-danger bg-opacity-10 me-3">
                    <HeartFill className="text-danger" />
                  </div>
                  <div>
                    <h6 className="mb-0">{t('homepage.real_connections')}</h6>
                    <small className="text-muted">{t('homepage.genuine_people')}</small>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="icon-circle bg-primary bg-opacity-10 me-3">
                    <PatchCheckFill className="text-primary" />
                  </div>
                  <div>
                    <h6 className="mb-0">{t('homepage.verified_profiles')}</h6>
                    <small className="text-muted">{t('homepage.safe_secure_platform')}</small>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <Card className="border-0 shadow-lg chat-preview bg-body-tertiary">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <div className="icon-circle-lg bg-primary bg-opacity-10 mx-auto mb-3">
                      <Chat size={40} className="text-primary" />
                    </div>
                    <h3 className="h4">{t('homepage.chat_anytime_anywhere')}</h3>
                  </div>
                  <p className="text-center mb-4">
                    {t('homepage.chat_description')}
                  </p>
                  <div className="d-flex justify-content-center gap-3">
                    <Badge bg="primary" className="px-3 py-2">{t('homepage.mobile')}</Badge>
                    <Badge bg="primary" className="px-3 py-2">{t('homepage.tablet')}</Badge>
                    <Badge bg="primary" className="px-3 py-2">{t('homepage.desktop')}</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="how-it-works py-5 bg-body-tertiary">
        <Container>
          <div className="text-center mb-5">
            <Badge bg="primary" className="mb-3">{t('homepage.simple_easy')}</Badge>
            <h2 className="display-5 fw-bold">{t('homepage.how_it_works')}</h2>
          </div>
          <Row className="g-4">
            <Col md={4}>
              <Card className="h-100 step-card text-center border-0 shadow-sm bg-body">
                <Card.Body className="p-5">
                  <div className="step-number-modern bg-gradient mb-4">
                    <span className="fs-2 fw-bold">1</span>
                  </div>
                  <h4 className="mb-3">{t('homepage.sign_up_seconds')}</h4>
                  <p className="text-muted">{t('homepage.sign_up_description')}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 step-card text-center border-0 shadow-sm bg-body position-relative">
                <div className="step-badge position-absolute top-0 start-50 translate-middle">
                  <Badge bg="danger" className="px-3 py-2">{t('homepage.most_popular')}</Badge>
                </div>
                <Card.Body className="p-5">
                  <div className="step-number-modern bg-gradient mb-4">
                    <span className="fs-2 fw-bold">2</span>
                  </div>
                  <h4 className="mb-3">{t('homepage.confirm_unlock')}</h4>
                  <p className="text-muted">{t('homepage.confirm_description')}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 step-card text-center border-0 shadow-sm bg-body">
                <Card.Body className="p-5">
                  <div className="step-number-modern bg-gradient mb-4">
                    <span className="fs-2 fw-bold">3</span>
                  </div>
                  <h4 className="mb-3">{t('homepage.start_chatting')}</h4>
                  <p className="text-muted">{t('homepage.start_chatting_description')}</p>
                  <div className="mt-3">
                    <Badge bg="success" className="px-3 py-2">
                      <Fire size={16} className="me-1" />
                      {t('homepage.free_credits')}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <div className="text-center mt-5">
            <p className="lead mb-4">{t('homepage.its_quick_easy_fun')}</p>
            <Button variant="danger" size="lg" className="px-5 py-3" onClick={handleGetStarted}>
              {t('homepage.sign_up_free_discreet')}
              <LightningFill className="ms-2" />
            </Button>
          </div>
        </Container>
      </section>

      {/* Why You'll Love It */}
      <section className="features-section py-5">
        <Container>
          <h2 className="text-center display-5 fw-bold mb-5">{t('homepage.why_youll_love_it')}</h2>
          <Row>
            <Col lg={3} md={6} className="mb-4">
              <div className="feature-item text-center">
                <div className="feature-icon-wrapper mb-3">
                  <HeartFill size={40} />
                </div>
                <h4>🔥 {t('homepage.steamy_community')}</h4>
                <p>{t('homepage.steamy_community_description')}</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="feature-item text-center">
                <div className="feature-icon-wrapper mb-3">
                  <ShieldFill size={40} />
                </div>
                <h4>{t('homepage.safe_private')}</h4>
                <p>{t('homepage.safe_private_description')}</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="feature-item text-center">
                <div className="feature-icon-wrapper mb-3">
                  <StarFill size={40} />
                </div>
                <h4>{t('homepage.always_fun')}</h4>
                <p>{t('homepage.always_fun_description')}</p>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4">
              <div className="feature-item text-center">
                <div className="feature-icon-wrapper mb-3">
                  <GraphUpArrow size={40} />
                </div>
                <h4>{t('homepage.totally_sexy')}</h4>
                <p>{t('homepage.totally_sexy_description')}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Life's Short Section */}
      <section className="life-short py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="display-5 fw-bold mb-4">{t('homepage.life_short')}</h2>
              <p className="lead mb-4">{t('homepage.too_hot_to_wait')}</p>
              <p className="mb-4">
                {t('homepage.local_singles_online', { whitelabelName: whitelabel.name })}
              </p>
              <p className="mb-4">
                {t('homepage.cute_stranger')}
              </p>
              <Button variant="primary" size="lg" onClick={handleGetStarted}>
                {t('homepage.take_the_leap')}
              </Button>
            </Col>
            <Col lg={6}>
              <div className="stats-container">
                <Row>
                  <Col md={4} className="mb-3">
                    <Card className="stat-card text-center h-100">
                      <Card.Body>
                        <h3 className="stat-number">{t('homepage.free_registration')}</h3>
                        <p className="stat-label">{t('homepage.free_registration_label')}</p>
                        <Button variant="link" onClick={handleGetStarted}>
                          {t('homepage.yes_i_want_in')}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Card className="stat-card text-center h-100">
                      <Card.Body>
                        <h3 className="stat-number">{t('homepage.female_profiles')}</h3>
                        <p className="stat-label">{t('homepage.female_profiles_label')}</p>
                        <Button variant="link" onClick={handleGetStarted}>
                          {t('homepage.show_me_ladies')}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4} className="mb-3">
                    <Card className="stat-card text-center h-100">
                      <Card.Body>
                        <h3 className="stat-number">{t('homepage.online_daily')}</h3>
                        <p className="stat-label">{t('homepage.online_daily_label')}</p>
                        <Button variant="link" onClick={handleGetStarted}>
                          {t('homepage.whos_on_now')}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="final-cta py-5 bg-primary text-white text-center">
        <Container>
          <h2 className="display-4 fw-bold mb-4">{t('homepage.hottest_singles_click_away')}</h2>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <>
              <Button variant="light" size="lg" onClick={openLogin}>
                {t('homepage.log_in')}
              </Button>
              <Button variant="outline-light" size="lg" onClick={handleGetStarted}>
                {t('homepage.join_free_now')}
              </Button>
            </>
          </div>
          <p className="mt-3 mb-0">{t('homepage.start_sexy_story')}</p>
        </Container>
      </section>

      {modals}
    </div>
  );
};

export default HomePage;
